import { ApiError } from "@sidegigllc/api";
import { type Attachment, attachmentsRetrieve, attachmentsUploadCreate, ProviderEnum } from "@sidegigllc/api/client";
import type { FileAttachment } from "api/resources/shared";
import React from "react";
import { isSameFile } from "utils/isSameFile";
import * as z from "zod/mini";

const MAX_FILE_SIZE = 40 * 1024 * 1024; // 40MB
const ACCEPTED_FILE_EXTENSIONS = ["gif", "jpeg", "png", "jpg", "pdf"];
export const INPUT_FILE_ACCEPT = ACCEPTED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(",");
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE / (1024 * 1024);

const attachmentSchema = z.instanceof(File).check(
  z.refine((file) => {
    if (!file) return true;
    return file.size <= MAX_FILE_SIZE;
  }, `File size must be less than ${MAX_FILE_SIZE_MB}MB`),
  z.refine((file) => {
    if (!file) return true;
    return ACCEPTED_FILE_EXTENSIONS.includes(file.name.split(".").pop() ?? "");
  }, `File type must one of ${INPUT_FILE_ACCEPT}`),
);

const MAX_ATTEMPTS = 10;
const INTERVAL_MS = 1000;
const pollAttachmentStatus = async (attachmentId: string): Promise<void> => {
  for (let attempts = 0; attempts < MAX_ATTEMPTS; attempts++) {
    try {
      const attachment = await attachmentsRetrieve(attachmentId);
      if (attachment.status === "rejected") {
        throw new Error("This file was rejected by the moderator");
      }
      if (attachment.status !== "pending") return;

      await new Promise((resolve) => {
        setTimeout(resolve, INTERVAL_MS);
      });
    } catch (error) {
      if (error instanceof Error && error.message.includes("rejected")) {
        throw error;
      }
      console.error("Error polling attachment status:", error);
      throw new Error("Failed to verify attachment status", { cause: error });
    }
  }
  throw new Error("Attachment verification timed out");
};

type UploadAttachmentSuccess = { status: "success"; file: File; attachment: FileAttachment };
type UploadAttachmentError = { status: "error"; error: string; file: File };
type ProcessedFile = UploadAttachmentSuccess | UploadAttachmentError;

// The upload/retrieve endpoints return the bare Attachment (no gig-relation `type`); default it to
// "DEFAULT" so the rest of the app keeps working with FileAttachment.
const toFileAttachment = (attachment: Attachment): FileAttachment => ({
  ...attachment,
  name: attachment.name ?? "",
  type: "DEFAULT",
});

const attachmentToFile = (attachment: FileAttachment): File => new File([], attachment.name);
const attachmentToUploadSuccess = (attachment: FileAttachment): UploadAttachmentSuccess => ({
  status: "success",
  file: attachmentToFile(attachment),
  attachment,
});

const onProcessFile = async (currentFile: File, uploadedFiles: Array<ProcessedFile>): Promise<ProcessedFile> => {
  try {
    // Before validating (a saved file's stand-in fails it), and by identity first: stand-ins share one name.
    let isCurrentFileAlreadyUploaded: ProcessedFile | undefined = uploadedFiles.find(
      ({ file }) => file === currentFile,
    );
    for (const file of isCurrentFileAlreadyUploaded ? [] : uploadedFiles) {
      if (await isSameFile(file.file, currentFile)) {
        isCurrentFileAlreadyUploaded = file;
        break;
      }
    }
    if (isCurrentFileAlreadyUploaded && "attachment" in isCurrentFileAlreadyUploaded) {
      const attachment = isCurrentFileAlreadyUploaded.attachment;
      return { status: "success", file: currentFile, attachment };
    }
    // check if file is valid
    const parsed = attachmentSchema.safeParse(currentFile);
    if (!parsed.success) {
      return { status: "error", error: parsed.error?.issues[0]?.message ?? "Invalid file", file: currentFile };
    }
    const uploadedFile = await attachmentsUploadCreate({
      file: currentFile,
      provider: ProviderEnum.CLOUDINARY,
      name: currentFile.name,
    });
    await pollAttachmentStatus(uploadedFile.id);
    return { status: "success", file: currentFile, attachment: toFileAttachment(uploadedFile) };
  } catch (error) {
    const message = ApiError.fromThrown(error).getMainErrorMessage() ?? "Unknown error";
    return {
      status: "error",
      error: message,
      file: currentFile,
    };
  }
};

type UseUploadAttachmentParams = {
  /**
   * Pre-existing attachments. Will only be set on initial mount
   */
  initialAttachments?: Array<FileAttachment> | null;
  onSuccess?: (attachmentIds: Array<FileAttachment>) => void;
};
export const useUploadAttachment = (params?: UseUploadAttachmentParams) => {
  const [error, setError] = React.useState<Array<UploadAttachmentError> | undefined>(undefined);
  const [isProcessing, setIsProcessing] = React.useState(false);
  // The listed files are the saved entries' own File objects, so a saved file is found by identity.
  const [saved] = React.useState(() => params?.initialAttachments?.map(attachmentToUploadSuccess) ?? []);
  const [files, setFiles] = React.useState<Array<File>>(saved.map(({ file }) => file));
  const [uploadedFiles, setUploadedFiles] = React.useState<Array<UploadAttachmentSuccess>>(saved);

  const onSelectFiles = async (currentFiles: Array<File>) => {
    if (!currentFiles || currentFiles.length === 0) {
      setError(undefined);
      setUploadedFiles([]);
      setFiles([]);
      params?.onSuccess?.([]);
      return;
    }

    setFiles(currentFiles);
    setError(undefined);
    setIsProcessing(true);
    const processedFiles = await Promise.all(currentFiles.map((file) => onProcessFile(file, uploadedFiles)));

    const successfulFiles = processedFiles.filter((result) => result.status === "success");
    const unsuccessfulFiles = processedFiles.filter((result) => result.status === "error");

    setError(unsuccessfulFiles);
    setUploadedFiles(successfulFiles);
    setIsProcessing(false);

    params?.onSuccess?.(successfulFiles.map((result) => result.attachment));
  };

  return {
    processing: isProcessing,
    error,
    files,
    onSelectFiles,
    uploadedFiles,
    setAttachments: (attachments: Array<FileAttachment>) => {
      const next = attachments.map(attachmentToUploadSuccess);
      setFiles(next.map(({ file }) => file));
      setUploadedFiles(next);
    },
  };
};
